import { supabase, isCloudMode } from '../supabase.ts';
import { pmStore, hydrateBoards } from './pm.svelte.ts';

type RealtimeChannel = ReturnType<NonNullable<typeof supabase>['channel']>;

let activeChannel: RealtimeChannel | null = null;

/**
 * Subscribe to real-time changes for a workspace.
 * Updates pmStore directly so the rest of the app stays reactive.
 */
export function subscribeWorkspace(workspaceId: string) {
  if (!isCloudMode || !supabase) return;

  // Clean up previous subscription
  if (activeChannel) {
    void supabase.removeChannel(activeChannel);
    activeChannel = null;
  }

  activeChannel = supabase
    .channel(`workspace:${workspaceId}`)
    // Card changes (insert / update / delete)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'cards',
      filter: `project_id=in.(${
        pmStore.projects
          .filter(p => p.workspace_id === workspaceId)
          .map(p => p.id)
          .join(',')
      })`,
    }, (payload) => {
      handleCardChange(payload as { eventType: string; new: Record<string, unknown>; old: Record<string, unknown> });
    })
    // Column changes
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'columns',
    }, () => {
      // Re-hydrate the active board on any column change
      if (pmStore.activeProjectId) void hydrateBoards(pmStore.activeProjectId);
    })
    // Project changes
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'projects',
      filter: `workspace_id=eq.${workspaceId}`,
    }, (payload) => {
      handleProjectChange(payload as { eventType: string; new: Record<string, unknown>; old: Record<string, unknown> });
    })
    .subscribe();
}

export function unsubscribeWorkspace() {
  if (!supabase || !activeChannel) return;
  void supabase.removeChannel(activeChannel);
  activeChannel = null;
}

function handleCardChange(payload: { eventType: string; new: Record<string, unknown>; old: Record<string, unknown> }) {
  const { eventType, new: newRow, old: oldRow } = payload;

  // Find which board detail contains this card and update it in-place
  for (const [boardId, detail] of Object.entries(pmStore.boardDetails)) {
    if (eventType === 'DELETE') {
      const cardId = (oldRow as { id: string }).id;
      pmStore.boardDetails[boardId] = {
        ...detail,
        columns: detail.columns.map(col => ({
          ...col,
          cards: col.cards.filter(c => c.id !== cardId),
        })),
      };
      return;
    }

    const card = newRow as { id: string; column_id: string; [key: string]: unknown };
    // Check if this board contains the card's project
    const hasCard = detail.columns.some(col => col.cards.some(c => c.id === card.id));
    const hasColumn = detail.columns.some(col => col.id === card.column_id);

    if (hasCard || hasColumn) {
      if (eventType === 'UPDATE') {
        pmStore.boardDetails[boardId] = {
          ...detail,
          columns: detail.columns.map(col => ({
            ...col,
            cards: col.cards.map(c => c.id === card.id ? { ...c, ...card } as typeof c : c),
          })),
        };

        // Handle column moves: move card to correct column
        const isInRightColumn = detail.columns.find(col => col.id === card.column_id)?.cards.some(c => c.id === card.id);
        if (!isInRightColumn) {
          // Re-hydrate to get correct column placement
          if (pmStore.activeProjectId) void hydrateBoards(pmStore.activeProjectId);
        }
      } else if (eventType === 'INSERT') {
        // Re-hydrate to get the new card in the right column
        if (pmStore.activeProjectId) void hydrateBoards(pmStore.activeProjectId);
      }
      return;
    }
  }
}

function handleProjectChange(payload: { eventType: string; new: Record<string, unknown>; old: Record<string, unknown> }) {
  const { eventType, new: newRow, old: oldRow } = payload;

  if (eventType === 'DELETE') {
    const deletedId = (oldRow as { id: string }).id;
    pmStore.projects = pmStore.projects.filter(p => p.id !== deletedId);
    return;
  }

  const project = newRow as unknown as typeof pmStore.projects[number];

  if (eventType === 'INSERT') {
    pmStore.projects = [...pmStore.projects, project];
  } else if (eventType === 'UPDATE') {
    pmStore.projects = pmStore.projects.map(p => p.id === project.id ? project : p);
  }
}
